/* EV2 — "Why?" 근거 리포트 카드(qa/stg·운영 검수용).
 *
 * evidence MCP 툴의 structured_content 를 렌더한다: sort·검색조건(적용/완화)·
 * 제품별 노출 플래그(LG's Pick·spotlight·pinned)·항목별 score 분해(base+boosts−deboosts=total)·
 * recommendTrace. 자연어 해설은 채팅 말풍선(synthesize)이 별도 동반한다.
 */

interface ScoreBucketEntry {
  label?: string;
  value?: number;
  factor?: number;
  field?: string;
  rules?: string[];
  fv?: number;
  doc_value?: number;
}

interface BaseParts {
  lexical?: number;
  neural?: number;
}

interface ScoreBreakdown {
  total?: number;
  bm25_base?: number;
  base_parts?: BaseParts;
  boosts?: ScoreBucketEntry[];
  deboosts?: ScoreBucketEntry[];
  lgpick?: number;
}

interface PickRow {
  rank: number;
  sku: string;
  title?: string;
  pinned?: boolean;
  spotlights_order_no?: number | null;
  lgpick_flag?: string | null;
}

interface SortExplainEntry {
  field?: string;
  value?: unknown;
}

interface MatchedRule {
  rule_id?: string | number;
  name?: string;
  kind?: string;
  weight?: number;
}

interface ScoreRow {
  rank: number;
  sku: string;
  title?: string;
  score?: number | null;
  breakdown?: ScoreBreakdown;
  sort_explain?: SortExplainEntry[];
  matched_rules?: MatchedRule[];
  explain_lines?: string[];
}

interface EvidenceReportLabels {
  title?: string;
  sort?: string;
  criteria?: string;
  applied?: string;
  relaxed?: string;
  picks?: string;
  rank?: string;
  product?: string;
  lgPick?: string;
  spotlight?: string;
  pinned?: string;
  scoreBreakdown?: string;
  bm25Base?: string;
  boosts?: string;
  deboosts?: string;
  total?: string;
  appliedRules?: string;
  trace?: string;
  noData?: string;
}

interface EvidenceReportData {
  available?: boolean;
  turnNo?: number | null;
  sort?: Record<string, unknown> | string | null;
  criteria?: Record<string, unknown> | null;
  ranking?: Record<string, unknown> | null;
  picks?: PickRow[];
  scores?: ScoreRow[];
  trace?: Record<string, unknown> | null;
  flags?: Record<string, boolean>;
  labels?: EvidenceReportLabels;
}

interface Props {
  data: EvidenceReportData;
  sessionToken: string;
}

const L = (labels: EvidenceReportLabels | undefined, key: keyof EvidenceReportLabels, fb: string): string =>
  (labels && labels[key]) || fb;

function fmtNum(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Math.abs(n) >= 100 ? n.toFixed(1) : n.toFixed(2);
}

function sortLabel(sort: EvidenceReportData["sort"]): string {
  if (!sort) return "—";
  if (typeof sort === "string") return sort;
  return String(sort.value ?? sort.label ?? sort.field ?? "—");
}

/** 해소 카테고리 1줄 — family(root) + 확정 전시 leaf CT(MDMS lv2 축 폐기, RENEWAL_2 §6). */
function categoryLabel(cat: Record<string, unknown> | undefined): string | null {
  if (!cat) return null;
  const family = cat.family_code as string | undefined;
  const ct = cat.category_code as string | undefined;
  if (!family && !ct) return null;
  if (!ct) return String(family);
  if (!family) return String(ct);
  return `${family} › ${ct}`;
}

const cellStyle: React.CSSProperties = {
  padding: "4px 8px",
  borderBottom: "1px solid #eee",
  fontSize: 12,
  verticalAlign: "top",
};
const headStyle: React.CSSProperties = {
  ...cellStyle,
  fontWeight: 600,
  background: "#f6f7f9",
  borderBottom: "1px solid #ddd",
};
const sectionStyle: React.CSSProperties = { margin: "10px 0" };
const tagStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "1px 6px",
  borderRadius: 4,
  fontSize: 11,
  marginRight: 4,
  background: "#eef2ff",
  color: "#3730a3",
};

function CriteriaSummary({ data }: { data: EvidenceReportData }) {
  const labels = data.labels;
  const criteria = (data.criteria || {}) as Record<string, unknown>;
  const ranking = (data.ranking || criteria.ranking || {}) as Record<string, unknown>;
  const summary = (criteria.summary || {}) as Record<string, unknown>;
  const sort = (ranking.sort || {}) as Record<string, unknown>;
  const chain = ranking.sort_chain as string[] | undefined;
  const lgpick = ranking.lgpick as Record<string, unknown> | undefined;
  const fold = ranking.fold as Record<string, unknown> | undefined;
  const boost = ranking.boost_scope as Record<string, unknown> | undefined;
  const sp = ranking.spotlights as Record<string, unknown> | undefined;
  const constraints =
    (criteria.criteria as unknown[] | undefined) ||
    (criteria.constraints as unknown[] | undefined) ||
    [];
  const category = categoryLabel(criteria.category as Record<string, unknown> | undefined);
  const sortKey = sort.key ?? sortLabel(data.sort);
  return (
    <div style={sectionStyle}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        {L(labels, "criteria", "Search criteria")}
      </div>
      <div style={{ fontSize: 12, marginBottom: 4 }}>
        {category && (
          <span style={{ ...tagStyle, background: "#f3e8ff", color: "#6b21a8" }}>
            {category}
          </span>
        )}
        <span style={tagStyle}>
          {L(labels, "sort", "Sort")}: {String(sortKey)}
          {sort.explicit ? " (explicit)" : ""}
        </span>
        {Boolean(sort.kind) && (
          <span style={tagStyle}>kind={String(sort.kind)}</span>
        )}
        {Boolean(sort.label) && (
          <span style={tagStyle}>{String(sort.label)}</span>
        )}
        {summary.applied !== undefined && (
          <span style={tagStyle}>
            {L(labels, "applied", "Applied")}: {String(summary.applied)}
          </span>
        )}
        {summary.relaxed !== undefined && (
          <span style={{ ...tagStyle, background: "#fef3c7", color: "#92400e" }}>
            {L(labels, "relaxed", "Relaxed")}: {String(summary.relaxed)}
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, marginBottom: 4, color: "#6b7280" }}>
        {lgpick && (
          <span
            style={{
              ...tagStyle,
              marginBottom: 4,
              background: lgpick.active ? "#fee2e2" : "#f3f4f6",
              color: lgpick.active ? "#991b1b" : "#6b7280",
            }}
          >
            lgpick {lgpick.active ? "ON" : "OFF"}
            {lgpick.category ? ` · CT=${String(lgpick.category)}` : ""}
          </span>
        )}
        {fold?.active === true && (
          <span style={{ ...tagStyle, marginBottom: 4, background: "#e0f2fe", color: "#0369a1" }}>
            fold {String(fold.field ?? "")}
          </span>
        )}
        {boost?.calibration_divisor != null && (
          <span style={{ ...tagStyle, marginBottom: 4 }}>
            D={String(boost.calibration_divisor)} · boost={String(boost.boost_functions ?? 0)} ·
            deboost={String(boost.deboost_functions ?? 0)}
          </span>
        )}
        {Boolean(ranking.locale_sort_default) && (
          <span style={{ ...tagStyle, marginBottom: 4 }}>
            getCoveoSortDefault: {String(ranking.locale_sort_default)}
          </span>
        )}
        {sp && (
          <span
            style={{
              ...tagStyle,
              marginBottom: 4,
              background: sp.active ? "#dcfce7" : "#f3f4f6",
              color: sp.active ? "#166534" : "#6b7280",
            }}
          >
            spotlights {sp.active ? "ON" : "OFF"}
            {sp.flag ? ` · flag=${String(sp.flag)}` : ""}
            {sp.category ? ` · CT=${String(sp.category)}` : ""}
            {sp.pinned ? " · pinned" : ""}
          </span>
        )}
      </div>
      {Array.isArray(chain) && chain.length > 0 && (
        <div
          style={{
            fontSize: 10,
            fontFamily: "monospace",
            marginBottom: 4,
            color: "#374151",
            wordBreak: "break-word",
          }}
        >
          chain: {chain.join(" → ")}
        </div>
      )}
      {Array.isArray(constraints) && constraints.length > 0 && (
        <ul style={{ margin: "4px 0 0 16px", padding: 0, fontSize: 12 }}>
          {constraints.map((c, i) => {
            const cc = (c || {}) as Record<string, unknown>;
            const field = String(cc.field ?? cc.kind ?? "");
            const op = String(cc.op ?? "");
            const val = String(cc.value_display ?? cc.value ?? "");
            const status = String(cc.status ?? "");
            return (
              <li key={i} style={{ color: status === "relaxed" ? "#92400e" : "#374151" }}>
                {field} {op} {val}
                {status ? ` (${status})` : ""}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const ruleChipStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "0 5px",
  borderRadius: 4,
  fontSize: 10,
  marginBottom: 2,
  background: "#dcfce7",
  color: "#166534",
};
const bdCellLeft: React.CSSProperties = { padding: "1px 6px 1px 0", verticalAlign: "top" };
const bdCellRight: React.CSSProperties = {
  padding: "1px 0",
  verticalAlign: "top",
  textAlign: "right",
};

// QRF 동적 가점 중 개수형 필드(주문/조회수)만 '개' 단위. 평점·가격 등은 값만 표기.
const QRF_COUNT_FIELDS = new Set(["ec_qty_ordered", "ec_pdp_view_count_90d"]);

/** QRF 동적 가점 doc_value 표기 — 개수형은 'N개', 그 외(평점·가격)는 값만. 정적이면 null. */
function qrfCount(b: ScoreBucketEntry): string | null {
  if (b.doc_value === undefined) return null;
  const v = Number(b.doc_value);
  return QRF_COUNT_FIELDS.has(b.field ?? "") ? `${v}개` : `${v}`;
}

/** 가점/감점 1행(테이블) — 좌: 라벨·값(+QRF 개수), 우: 매핑 룰명(세로 나열). */
function BucketTableRow({
  text,
  color,
  count,
  rules,
}: {
  text: string;
  color: string;
  count?: string | null;
  rules?: string[];
}) {
  return (
    <tr>
      <td style={{ ...bdCellLeft, color }}>
        {text}
        {count && <span style={{ color: "#9ca3af" }}> ({count})</span>}
      </td>
      <td style={bdCellRight}>
        {(rules || []).map((name, j) => (
          <div key={`rn${j}`}>
            <span style={ruleChipStyle}>{name}</span>
          </div>
        ))}
      </td>
    </tr>
  );
}

/** 항목별 score 분해 블록 — base(+lexical/neural) / ▲Boosts·LG's Pick·▼Deboosts(2열 테이블) / Total.
 * 각 가점/감점 행 우측 열에 그 부스트를 만든 ranking_rule 룰명을 정렬 매핑한다(요구1).
 * QRF 동적 가점은 "(N개)"(raw count) 를 함께 노출한다(요구2).
 * 필드 정렬(newest 등)은 _score=0 이므로 sort_explain(정렬 키값)을 근거로 표시. */
function ScoreBreakdownBlock({
  score,
  labels,
}: {
  score: ScoreRow | undefined;
  labels: EvidenceReportLabels | undefined;
}) {
  const bd = score?.breakdown;
  const sortExplain = score?.sort_explain;
  const hasSort = !bd && Array.isArray(sortExplain) && sortExplain.length > 0;

  if (hasSort) {
    return (
      <div style={{ fontSize: 11, lineHeight: 1.6 }}>
        {sortExplain!.map((s, i) => {
          const last = i === sortExplain!.length - 1;
          return (
            <div key={`s${i}`} style={last ? { fontWeight: 600 } : { color: "#9ca3af" }}>
              {last ? "↓ " : "· "}
              {s.field}: {String(s.value ?? "—")}
            </div>
          );
        })}
      </div>
    );
  }
  if (!bd) {
    return <span style={{ fontSize: 11 }}>{fmtNum(score?.score)}</span>;
  }
  const hasBoostHeader =
    (bd.boosts && bd.boosts.length > 0) ||
    (bd.lgpick !== undefined && bd.lgpick !== null);
  return (
    <div style={{ fontSize: 11, lineHeight: 1.6 }}>
      <div>
        {L(labels, "bm25Base", "Base relevance")}: {fmtNum(bd.bm25_base)}
      </div>
      {bd.base_parts && (
        <div style={{ marginLeft: 10, color: "#9ca3af" }}>
          <div>· lexical: {fmtNum(bd.base_parts.lexical)}</div>
          <div>· neural: {fmtNum(bd.base_parts.neural)}</div>
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 2 }}>
        <tbody>
          {hasBoostHeader && (
            <tr>
              <td style={{ ...bdCellLeft, fontWeight: 600, color: "#166534" }}>
                ▲ {L(labels, "boosts", "Boosts")}
              </td>
              <td style={{ ...bdCellRight, fontWeight: 600, color: "#6b7280" }}>
                {L(labels, "appliedRules", "Applied rules")}
              </td>
            </tr>
          )}
          {(bd.boosts || []).map((b, i) => (
            <BucketTableRow
              key={`b${i}`}
              text={`+ ${b.label}: ${fmtNum(b.value)}`}
              color="#166534"
              count={qrfCount(b)}
              rules={b.rules}
            />
          ))}
          {bd.lgpick !== undefined && bd.lgpick !== null && (
            <BucketTableRow
              text={`+ ${L(labels, "lgPick", "LG's Pick")}: ${fmtNum(bd.lgpick)}`}
              color="#991b1b"
            />
          )}
          {bd.deboosts && bd.deboosts.length > 0 && (
            <tr>
              <td style={{ ...bdCellLeft, fontWeight: 600, color: "#92400e" }} colSpan={2}>
                ▼ {L(labels, "deboosts", "Deboosts")}
              </td>
            </tr>
          )}
          {(bd.deboosts || []).map((d, i) => (
            <BucketTableRow
              key={`d${i}`}
              text={`× ${d.label}: ${fmtNum(d.factor)}`}
              color="#92400e"
              rules={d.rules}
            />
          ))}
        </tbody>
      </table>
      <div style={{ fontWeight: 600, borderTop: "1px solid #eee", marginTop: 2 }}>
        {L(labels, "total", "Total")}: {fmtNum(bd.total ?? score?.score)}
      </div>
    </div>
  );
}

function RankReasonTable({ data }: { data: EvidenceReportData }) {
  const labels = data.labels;
  const picks = data.picks || [];
  const scoresByRank = new Map<number, ScoreRow>();
  (data.scores || []).forEach((s) => scoresByRank.set(s.rank, s));
  if (picks.length === 0) return null;

  return (
    <div style={sectionStyle}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        {L(labels, "picks", "Ranked products")}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...headStyle, width: 36, textAlign: "left" }}>
              {L(labels, "rank", "Rank")}
            </th>
            <th style={{ ...headStyle, textAlign: "left" }}>
              {L(labels, "product", "Product")}
            </th>
          </tr>
        </thead>
        <tbody>
          {picks.map((p) => {
            const score = scoresByRank.get(p.rank);
            return (
              <tr key={p.sku || p.rank}>
                <td style={{ ...cellStyle, fontWeight: 600 }}>#{p.rank}</td>
                <td style={cellStyle}>
                  <div style={{ fontWeight: 600 }}>{p.title || p.sku}</div>
                  <div style={{ color: "#9ca3af", fontSize: 11 }}>{p.sku}</div>
                  <div style={{ marginTop: 2 }}>
                    {String(p.lgpick_flag || "").toUpperCase() === "Y" && (
                      <span style={{ ...tagStyle, background: "#fee2e2", color: "#991b1b" }}>
                        {L(labels, "lgPick", "LG's Pick")}
                      </span>
                    )}
                    {p.spotlights_order_no !== null && p.spotlights_order_no !== undefined && (
                      <span style={{ ...tagStyle, background: "#dcfce7", color: "#166534" }}>
                        {L(labels, "spotlight", "Spotlight")} #{p.spotlights_order_no}
                      </span>
                    )}
                    {p.pinned && (
                      <span style={tagStyle}>{L(labels, "pinned", "Pinned")}</span>
                    )}
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      paddingTop: 6,
                      borderTop: "1px dashed #eee",
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 2 }}>
                      {L(labels, "scoreBreakdown", "Score breakdown")}
                    </div>
                    <ScoreBreakdownBlock score={score} labels={labels} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TraceBlock({ data }: { data: EvidenceReportData }) {
  const labels = data.labels;
  const trace = data.trace;
  if (!trace || Object.keys(trace).length === 0) return null;
  return (
    <details style={sectionStyle}>
      <summary style={{ fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
        {L(labels, "trace", "Recommend trace")}
      </summary>
      <pre
        style={{
          fontSize: 11,
          background: "#f6f7f9",
          padding: 8,
          borderRadius: 4,
          overflowX: "auto",
          margin: "4px 0 0",
        }}
      >
        {JSON.stringify(trace, null, 2)}
      </pre>
    </details>
  );
}

export default function EvidenceReportCard({ data }: Props) {
  const labels = data.labels;
  const hasContent =
    data.available !== false &&
    ((data.picks && data.picks.length > 0) || data.criteria || data.trace);

  return (
    <div className="ac-evidence-report">
      <div className="ac-evidence-report__header">
        {L(labels, "title", "Why these ranked this way")}
        {data.turnNo !== null && data.turnNo !== undefined && (
          <span className="ac-evidence-report__turn"> · turn {data.turnNo}</span>
        )}
      </div>
      {hasContent ? (
        <div className="ac-evidence-report__content">
          <CriteriaSummary data={data} />
          <RankReasonTable data={data} />
          <TraceBlock data={data} />
        </div>
      ) : (
        <div className="ac-evidence-report__empty">
          {L(labels, "noData", "No evidence available for this turn.")}
        </div>
      )}
    </div>
  );
}
